import { useState, useEffect, useRef, useMemo, Component, type ReactNode } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { useSearchParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Plus,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Users,
  X,
  Search,
  ChevronRight,
  ArrowLeft,
  FlaskConical,
  Loader2,
} from 'lucide-react'

type ViewMode = 'list' | 'detail'
type ListFilter = 'all' | 'experiments'
type SortMode = 'newest' | 'oldest' | 'most-users'

export default function FlagsPage({ embedded = false }: { embedded?: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [selectedFlagId, setSelectedFlagId] = useState<Id<'featureFlags'> | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [listFilter, setListFilter] = useState<ListFilter>('all')
  const [sortMode, setSortMode] = useState<SortMode>('newest')
  const [newFlag, setNewFlag] = useState({ key: '', name: '', description: '', type: 'boolean' as 'boolean' | 'string', defaultValue: '' })

  const flags = useQuery(api.featureFlags.list)

  // Sync URL ?flag=<key> → selection on load
  const flagParam = searchParams.get('flag')
  useEffect(() => {
    if (!flags || !flagParam) return
    const match = flags.find(f => f.key === flagParam)
    if (match && match._id !== selectedFlagId) {
      setSelectedFlagId(match._id)
      setViewMode('detail')
    }
  }, [flags, flagParam]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectFlag = (id: Id<'featureFlags'>) => {
    const flag = flags?.find(f => f._id === id)
    setSelectedFlagId(id)
    setViewMode('detail')
    if (flag) setSearchParams({ flag: flag.key }, { replace: true })
  }

  const clearSelection = () => {
    setSelectedFlagId(null)
    setViewMode('list')
    setSearchParams({}, { replace: true })
  }
  const selectedFlag = useQuery(
    api.featureFlags.get,
    selectedFlagId ? { flagId: selectedFlagId } : 'skip'
  )

  const createFlag = useMutation(api.featureFlags.create)
  const updateFlag = useMutation(api.featureFlags.update)
  const removeFlag = useMutation(api.featureFlags.remove)
  const addUser = useMutation(api.featureFlags.addUser)
  const removeUser = useMutation(api.featureFlags.removeUser)
  const addValue = useMutation(api.featureFlags.addValue)
  const removeValue = useMutation(api.featureFlags.removeValue)

  const handleCreateFlag = async () => {
    if (!newFlag.key || !newFlag.name) return
    const defaultValue = newFlag.type === 'boolean' ? 'off' : newFlag.defaultValue
    if (!defaultValue) return
    await createFlag({
      key: newFlag.key,
      name: newFlag.name,
      description: newFlag.description || undefined,
      type: newFlag.type,
      defaultValue,
      values: newFlag.type === 'string' && newFlag.defaultValue ? [newFlag.defaultValue] : undefined,
    })
    setNewFlag({ key: '', name: '', description: '', type: 'boolean', defaultValue: '' })
    setShowCreateForm(false)
  }

  const handleToggleFlag = async (flagId: Id<'featureFlags'>, currentEnabled: boolean) => {
    await updateFlag({ flagId, enabled: !currentEnabled })
  }

  const handleDeleteFlag = async (flagId: Id<'featureFlags'>) => {
    if (confirm('Delete this flag and all user assignments?')) {
      await removeFlag({ flagId })
      if (selectedFlagId === flagId) {
        clearSelection()
      }
    }
  }

  if (viewMode === 'detail' && selectedFlagId) {
    return (
      <div className={embedded ? 'max-w-5xl mx-auto' : 'p-6 max-w-5xl mx-auto'}>
        <FlagDetailView
          flagId={selectedFlagId}
          flag={selectedFlag}
          onBack={clearSelection}
          onToggle={handleToggleFlag}
          onDelete={handleDeleteFlag}
          onUpdate={(args) => updateFlag(args)}
          onAddUser={addUser}
          onRemoveUser={removeUser}
          onAddValue={addValue}
          onRemoveValue={removeValue}
        />
      </div>
    )
  }

  return (
    <div className={cn(embedded ? 'max-w-5xl mx-auto space-y-6' : 'p-6 max-w-5xl mx-auto space-y-6')}>
      <div className="flex justify-between items-center">
        {!embedded && (
          <div>
            <h1 className="text-lg font-semibold">Feature Flags</h1>
            <p className="text-sm text-muted-foreground">Manage flags and experiments</p>
          </div>
        )}
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Flag
        </Button>
      </div>

      {showCreateForm && (
        <div className="border border-border rounded-lg bg-card p-5">
          <h3 className="text-base font-semibold mb-4">Create Feature Flag</h3>
          <div className="grid gap-4 max-w-md">
            <div className="grid gap-2">
              <Label htmlFor="key">Key</Label>
              <Input id="key" placeholder="e.g., video_provider" value={newFlag.key} onChange={(e) => setNewFlag({ ...newFlag, key: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" placeholder="e.g., Video Provider" value={newFlag.name} onChange={(e) => setNewFlag({ ...newFlag, name: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Input id="description" placeholder="What does this flag control?" value={newFlag.description} onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Type</Label>
              <div className="flex gap-2">
                <Button variant={newFlag.type === 'boolean' ? 'default' : 'outline'} size="sm" onClick={() => setNewFlag({ ...newFlag, type: 'boolean', defaultValue: '' })}>Boolean</Button>
                <Button variant={newFlag.type === 'string' ? 'default' : 'outline'} size="sm" onClick={() => setNewFlag({ ...newFlag, type: 'string' })}>String</Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {newFlag.type === 'boolean' ? 'Values: on / off. Default: off.' : 'Custom values with a default.'}
              </p>
            </div>
            {newFlag.type === 'string' && (
              <div className="grid gap-2">
                <Label htmlFor="defaultValue">Default Value</Label>
                <Input id="defaultValue" placeholder="e.g., veo" value={newFlag.defaultValue} onChange={(e) => setNewFlag({ ...newFlag, defaultValue: e.target.value })} />
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleCreateFlag} disabled={!newFlag.key || !newFlag.name || (newFlag.type === 'string' && !newFlag.defaultValue)}>Create</Button>
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      <FlagList
        flags={flags}
        listFilter={listFilter}
        setListFilter={setListFilter}
        sortMode={sortMode}
        setSortMode={setSortMode}
        onSelect={selectFlag}
        onToggle={handleToggleFlag}
        onDelete={handleDeleteFlag}
      />
    </div>
  )
}

// ── Flag List ─────────────────────────────────────────────────────────────────

function isExperimentFlag(flag: any): 'active' | 'concluded' | false {
  const active = flag.rolloutMode === 'hash' && flag.weights && Object.keys(flag.weights as object).length >= 2
  if (active) return 'active'
  if (flag.controlVariant) return 'concluded'
  return false
}

function FlagList({
  flags, listFilter, setListFilter, sortMode, setSortMode, onSelect, onToggle, onDelete,
}: {
  flags: any[] | undefined
  listFilter: ListFilter
  setListFilter: (f: ListFilter) => void
  sortMode: SortMode
  setSortMode: (s: SortMode) => void
  onSelect: (id: Id<'featureFlags'>) => void
  onToggle: (id: Id<'featureFlags'>, enabled: boolean) => void
  onDelete: (id: Id<'featureFlags'>) => void
}) {
  const filtered = useMemo(() => {
    if (!flags) return undefined
    let items = flags
    if (listFilter === 'experiments') {
      items = items.filter(f => isExperimentFlag(f) !== false)
    }
    items = [...items].sort((a, b) => {
      if (listFilter === 'experiments') {
        const aStatus = isExperimentFlag(a)
        const bStatus = isExperimentFlag(b)
        if (aStatus === 'active' && bStatus !== 'active') return -1
        if (bStatus === 'active' && aStatus !== 'active') return 1
      }
      switch (sortMode) {
        case 'oldest': return (a.firstExposureAt ?? a.createdAt) - (b.firstExposureAt ?? b.createdAt)
        case 'most-users': return (b.userCount ?? 0) - (a.userCount ?? 0)
        default: return (b.firstExposureAt ?? b.createdAt) - (a.firstExposureAt ?? a.createdAt)
      }
    })
    return items
  }, [flags, listFilter, sortMode])

  const experimentCount = useMemo(() => flags?.filter(f => isExperimentFlag(f) !== false).length ?? 0, [flags])

  return (
    <div className="border border-border rounded-lg bg-card">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <div className="flex gap-1">
          {(['all', 'experiments'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setListFilter(tab)}
              className={cn(
                'px-3 py-1.5 rounded text-xs font-medium transition-colors',
                listFilter === tab
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              {tab === 'all' ? `All (${flags?.length ?? 0})` : `Experiments (${experimentCount})`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground mr-1">Sort</span>
          {([['newest', 'Newest'], ['oldest', 'Oldest'], ['most-users', 'Users']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSortMode(key)}
              className={cn(
                'px-2 py-0.5 rounded text-xs transition-colors',
                sortMode === key
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {!filtered ? (
        <p className="text-sm text-muted-foreground text-center py-8">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          {listFilter === 'experiments' ? 'No experiments yet.' : 'No feature flags yet.'}
        </p>
      ) : (
        <div className="divide-y divide-border">
          {filtered.map((flag) => {
            const expStatus = isExperimentFlag(flag)
            const startDate = flag.firstExposureAt ? new Date(flag.firstExposureAt).toLocaleDateString('en-CA') : null
            return (
              <div
                key={flag._id}
                className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => onSelect(flag._id)}
              >
                <div className="flex items-center gap-3 flex-1">
                  <button onClick={(e) => { e.stopPropagation(); onToggle(flag._id, flag.enabled) }}>
                    {flag.enabled ? <ToggleRight className="h-6 w-6 text-teal-600" /> : <ToggleLeft className="h-6 w-6 text-muted-foreground" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{flag.name}</p>
                      {expStatus === 'active' && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-600 font-medium">
                          <FlaskConical className="h-3 w-3" /> experiment
                        </span>
                      )}
                      {expStatus === 'concluded' && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 font-medium">
                          <FlaskConical className="h-3 w-3" /> concluded
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs text-muted-foreground font-mono">{flag.key}</p>
                      {!expStatus && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">{flag.type ?? 'boolean'}</span>
                      )}
                      {expStatus && startDate && (
                        <span className="text-xs text-muted-foreground">started {startDate}</span>
                      )}
                      <span className="text-xs text-muted-foreground">{flag.userCount} user{flag.userCount !== 1 ? 's' : ''}</span>
                      {!expStatus && (
                        <span className="text-xs text-muted-foreground">default: <span className="font-mono">{flag.value ?? 'off'}</span></span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={(e) => { e.stopPropagation(); onDelete(flag._id) }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Flag Detail View ──────────────────────────────────────────────────────────

function FlagDetailView({
  flagId, flag, onBack, onToggle, onDelete, onUpdate, onAddUser, onRemoveUser, onAddValue, onRemoveValue,
}: {
  flagId: Id<'featureFlags'>
  flag: any
  onBack: () => void
  onToggle: (flagId: Id<'featureFlags'>, enabled: boolean) => void
  onDelete: (flagId: Id<'featureFlags'>) => void
  onUpdate: (args: any) => Promise<unknown>
  onAddUser: (args: { flagId: Id<'featureFlags'>; userId: Id<'users'>; option?: string }) => Promise<any>
  onRemoveUser: (args: { flagId: Id<'featureFlags'>; userId: Id<'users'> }) => Promise<unknown>
  onAddValue: (args: { flagId: Id<'featureFlags'>; value: string }) => Promise<unknown>
  onRemoveValue: (args: { flagId: Id<'featureFlags'>; value: string }) => Promise<unknown>
}) {
  const [newValueInput, setNewValueInput] = useState('')
  const [activeTab, setActiveTab] = useState<'config' | 'results'>('config')

  if (!flag) return <p className="text-muted-foreground">Loading...</p>

  const flagType = flag.type ?? 'boolean'
  const knownValues: string[] = flag.options ?? (flagType === 'boolean' ? ['on', 'off'] : [])
  const defaultValue = flag.value ?? (flagType === 'boolean' ? 'off' : knownValues[0])
  const isActiveExperiment = flag.rolloutMode === 'hash' && flag.weights && Object.keys(flag.weights as object).length >= 2
  const isConcludedExperiment = !isActiveExperiment && !!(flag as any).controlVariant
  const isHashMode = flag.rolloutMode === 'hash'

  const allUsers = (flag.users ?? []).map((u: any) => ({ ...u, option: u.option ?? defaultValue }))
  const manualUsers = allUsers.filter((u: any) => u.source !== 'hash')
  const orphanedValues = [...new Set(allUsers.map((u: any) => u.option).filter((o: string) => o && !knownValues.includes(o)))] as string[]
  const values = [...knownValues, ...orphanedValues]

  const handleAddValue = async () => {
    const trimmed = newValueInput.trim()
    if (!trimmed) return
    await onAddValue({ flagId, value: trimmed })
    setNewValueInput('')
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">{flag.name}</h1>
            {isActiveExperiment && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-600 font-medium">
                <FlaskConical className="h-3 w-3" /> experiment
              </span>
            )}
            {isConcludedExperiment && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 font-medium">
                <FlaskConical className="h-3 w-3" /> concluded
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground font-mono">{flag.key}</p>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">{flagType}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => onToggle(flagId, flag.enabled)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border hover:bg-muted">
            {flag.enabled
              ? <><ToggleRight className="h-5 w-5 text-teal-600" /><span className="text-sm font-medium text-teal-600">Enabled</span></>
              : <><ToggleLeft className="h-5 w-5 text-muted-foreground" /><span className="text-sm font-medium text-muted-foreground">Disabled</span></>}
          </button>
          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950" onClick={() => onDelete(flagId)}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </div>
      </div>

      {flag.description && <p className="text-muted-foreground">{flag.description}</p>}

      <div className="flex gap-1 border-b border-border">
        <button onClick={() => setActiveTab('config')} className={cn('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors', activeTab === 'config' ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
          Configuration
        </button>
        <button onClick={() => setActiveTab('results')} className={cn('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors', activeTab === 'results' ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
          Experiment Results
        </button>
      </div>

      {activeTab === 'config' ? (
        <div className="space-y-6">
          <ExperimentConfig flagId={flagId} flag={flag} values={knownValues} onUpdate={onUpdate} />

          {values.map((val: string) => {
            const isDefault = val === defaultValue
            const usersInValue = manualUsers.filter((u: any) => u.option === val)
            const hashCount = isHashMode ? allUsers.filter((u: any) => u.option === val && u.source === 'hash').length : 0
            return (
              <ValueCard
                key={val} flagId={flagId} value={val} isDefault={isDefault}
                canRemove={flagType === 'string' && !isDefault}
                users={usersInValue} hashCount={hashCount}
                weight={isHashMode && flag.weights ? (flag.weights as Record<string, number>)[val] : undefined}
                onSetDefault={() => onUpdate({ flagId, defaultValue: val })}
                onRemoveValue={() => { if (confirm(`Remove value "${val}"?`)) onRemoveValue({ flagId, value: val }) }}
                onAddUser={(userId) => onAddUser({ flagId, userId, option: val })}
                onRemoveUser={(userId) => onRemoveUser({ flagId, userId })}
              />
            )
          })}

          {flagType === 'string' && (
            <div className="flex items-center gap-2 max-w-sm">
              <Input placeholder="Add value..." value={newValueInput} onChange={(e) => setNewValueInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddValue()} className="text-sm" />
              <Button size="sm" variant="outline" onClick={handleAddValue} disabled={!newValueInput.trim()}><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
          )}
        </div>
      ) : (
        <ExperimentResultsWithWindow flagKey={flag.key} flag={flag} />
      )}
    </div>
  )
}

// ── Experiment Configuration ──────────────────────────────────────────────────

function ExperimentConfig({ flagId, flag, values, onUpdate }: {
  flagId: Id<'featureFlags'>; flag: any; values: string[]; onUpdate: (args: any) => Promise<unknown>
}) {
  const rolloutMode = flag.rolloutMode ?? 'manual'
  const weights = (flag.weights as Record<string, number> | undefined) ?? {}
  const [editingWeights, setEditingWeights] = useState<Record<string, string>>({})
  const [isEditingWeights, setIsEditingWeights] = useState(false)
  const [footprintInput, setFootprintInput] = useState('')

  const controlVariant: string | undefined = (flag as any).controlVariant
  const footprint: string[] = ((flag as any).footprint as string[] | undefined) ?? []
  const variantDescriptions: Record<string, string> = ((flag as any).variantDescriptions as Record<string, string> | undefined) ?? {}
  const [editingDescs, setEditingDescs] = useState<Record<string, string>>({})
  const [isEditingDescs, setIsEditingDescs] = useState(false)

  const handleRolloutChange = async (mode: string) => {
    if (mode === 'hash' && values.length >= 2) {
      const equalWeight = Math.floor(100 / values.length)
      const newWeights: Record<string, number> = {}
      values.forEach((v, i) => { newWeights[v] = i === 0 ? 100 - equalWeight * (values.length - 1) : equalWeight })
      await onUpdate({ flagId, rolloutMode: 'hash', weights: newWeights })
    } else {
      await onUpdate({ flagId, rolloutMode: mode })
    }
  }

  const startEditingWeights = () => {
    const current: Record<string, string> = {}
    for (const v of values) current[v] = String(weights[v] ?? 0)
    setEditingWeights(current)
    setIsEditingWeights(true)
  }

  const saveWeights = async () => {
    const parsed: Record<string, number> = {}
    for (const [k, v] of Object.entries(editingWeights)) parsed[k] = Math.max(0, parseInt(v) || 0)
    await onUpdate({ flagId, weights: parsed })
    setIsEditingWeights(false)
  }

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <div className="p-5 border-b border-border">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Rollout &amp; Experiment</h3>

        <div className="flex items-center gap-3 mb-4">
          <Label className="text-sm w-28 shrink-0">Rollout mode</Label>
          <div className="flex gap-2">
            <button onClick={() => handleRolloutChange('manual')} className={cn('px-3 py-1.5 text-xs rounded border transition-colors', rolloutMode === 'manual' ? 'bg-foreground text-background border-foreground' : 'bg-background border-border hover:bg-muted')}>Manual</button>
            <button onClick={() => handleRolloutChange('hash')} disabled={values.length < 2} className={cn('px-3 py-1.5 text-xs rounded border transition-colors', rolloutMode === 'hash' ? 'bg-foreground text-background border-foreground' : 'bg-background border-border hover:bg-muted', values.length < 2 && 'opacity-40 cursor-not-allowed')}>Hash (A/B)</button>
          </div>
          {values.length < 2 && <span className="text-xs text-muted-foreground">Need 2+ values for hash rollout</span>}
        </div>

        {rolloutMode === 'hash' && (
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <Label className="text-sm w-28 shrink-0">Weights</Label>
              {!isEditingWeights ? (
                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    {values.map(v => <span key={v} className="text-xs font-mono px-2 py-1 rounded bg-muted">{v}: {weights[v] ?? 0}%</span>)}
                  </div>
                  <Button size="sm" variant="ghost" className="text-xs h-7" onClick={startEditingWeights}>Edit</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  {values.map(v => (
                    <div key={v} className="flex items-center gap-1">
                      <span className="text-xs font-mono text-muted-foreground">{v}:</span>
                      <Input type="number" min={0} max={100} value={editingWeights[v] ?? '0'} onChange={e => setEditingWeights({ ...editingWeights, [v]: e.target.value })} className="w-16 h-7 text-xs" />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                  ))}
                  <Button size="sm" className="h-7 text-xs" onClick={saveWeights}>Save</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setIsEditingWeights(false)}>Cancel</Button>
                </div>
              )}
            </div>
            <div className="ml-[124px]">
              <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                {values.map((v, i) => {
                  const w = weights[v] ?? 0
                  const total = Object.values(weights).reduce((s: number, n) => s + (n as number), 0)
                  const pct = total > 0 ? (w / total) * 100 : 0
                  const colors = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500']
                  return <div key={v} className={cn('transition-all', colors[i % colors.length])} style={{ width: `${pct}%` }} title={`${v}: ${pct.toFixed(0)}%`} />
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {rolloutMode === 'hash' && (
        <div className="p-5 space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-1">Experiment Settings</h3>

          <div className="flex items-center gap-3">
            <Label className="text-sm w-28 shrink-0">Control variant</Label>
            <Select value={controlVariant ?? '__none__'} onValueChange={(v) => onUpdate({ flagId, controlVariant: v === '__none__' ? '' : v })}>
              <SelectTrigger className="w-64 h-8 text-xs"><SelectValue placeholder="Select control..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__"><span className="text-muted-foreground">Auto (first alphabetically)</span></SelectItem>
                {values.map(v => <SelectItem key={v} value={v}><span className="font-mono">{v}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start gap-3">
            <Label className="text-sm w-28 shrink-0 pt-1">Footprint</Label>
            <div className="flex-1 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {footprint.map(token => (
                  <span key={token} className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded bg-muted">
                    {token}
                    <button onClick={() => onUpdate({ flagId, footprint: footprint.filter(t => t !== token) })} className="text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                {footprint.length === 0 && <span className="text-xs text-muted-foreground">No footprint tokens</span>}
              </div>
              <div className="flex items-center gap-2 max-w-sm">
                <Input placeholder="e.g., prompt:execution_patterns" value={footprintInput} onChange={(e) => setFootprintInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { const t = footprintInput.trim(); if (t && !footprint.includes(t)) { onUpdate({ flagId, footprint: [...footprint, t] }); setFootprintInput('') } } }} className="text-xs h-7" />
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { const t = footprintInput.trim(); if (t && !footprint.includes(t)) { onUpdate({ flagId, footprint: [...footprint, t] }); setFootprintInput('') } }} disabled={!footprintInput.trim()}>Add</Button>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Label className="text-sm w-28 shrink-0 pt-1">Variant info</Label>
            <div className="flex-1 space-y-2">
              {!isEditingDescs ? (
                <>
                  {values.map(v => (
                    <div key={v} className="text-xs">
                      <span className="font-mono text-foreground/70">{v}</span>
                      {variantDescriptions[v] ? (
                        <span className="ml-1.5 text-muted-foreground">— {variantDescriptions[v]}</span>
                      ) : (
                        <span className="ml-1.5 text-muted-foreground/50 italic">no description</span>
                      )}
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => {
                    const current: Record<string, string> = {}
                    for (const v of values) current[v] = variantDescriptions[v] ?? ''
                    setEditingDescs(current)
                    setIsEditingDescs(true)
                  }}>Edit</Button>
                </>
              ) : (
                <>
                  {values.map(v => (
                    <div key={v} className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground w-24 shrink-0">{v}</span>
                      <Input
                        placeholder="One-line description of this variant"
                        value={editingDescs[v] ?? ''}
                        onChange={e => setEditingDescs({ ...editingDescs, [v]: e.target.value })}
                        className="text-xs h-7 flex-1"
                      />
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs" onClick={() => {
                      const cleaned: Record<string, string> = {}
                      for (const [k, v] of Object.entries(editingDescs)) {
                        if (v.trim()) cleaned[k] = v.trim()
                      }
                      onUpdate({ flagId, variantDescriptions: cleaned })
                      setIsEditingDescs(false)
                    }}>Save</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setIsEditingDescs(false)}>Cancel</Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

class QueryErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      const isDocLimit = this.state.error.message?.includes('Too many documents')
      return (
        <div className="border border-border rounded-lg bg-card p-8 text-center space-y-3">
          <p className="text-sm text-muted-foreground">{isDocLimit ? 'Too much data for this window — try a shorter time range.' : 'Failed to load experiment results.'}</p>
          <p className="text-xs text-muted-foreground font-mono">{this.state.error.message?.slice(0, 120)}</p>
          <button onClick={() => this.setState({ error: null })} className="text-xs text-blue-600 hover:underline">Try again</button>
        </div>
      )
    }
    return this.props.children
  }
}

// ── Experiment Results ─────────────────────────────────────────────────────────

const WINDOW_OPTIONS = [
  { label: '3d', days: 3 },
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: 'All', days: 0 },
]

function ExperimentResultsWithWindow({ flagKey, flag }: { flagKey: string; flag: any }) {
  const [windowDays, setWindowDays] = useState(7)
  const isActiveExperiment = flag.rolloutMode === 'hash' && flag.weights && Object.keys(flag.weights as object).length >= 2
  const distinctVariants = useMemo(() => {
    const opts = new Set<string>()
    for (const u of flag.users ?? []) { if (u.option) opts.add(u.option) }
    return opts.size
  }, [flag.users])
  const hasExperimentData = isActiveExperiment || distinctVariants >= 2

  if (!hasExperimentData) {
    return (
      <div className="border border-border rounded-lg bg-card p-8 text-center">
        <FlaskConical className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Enable hash rollout with 2+ variants to run an experiment.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {!isActiveExperiment && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 font-medium">
            concluded
          </span>
        )}
        <div className="flex items-center gap-1 ml-auto">
          <span className="text-xs text-muted-foreground mr-1">Window</span>
          {WINDOW_OPTIONS.map(opt => (
            <button
              key={opt.days}
              onClick={() => setWindowDays(opt.days)}
              className={cn(
                'px-2 py-0.5 rounded text-xs font-mono transition-colors',
                windowDays === opt.days
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <QueryErrorBoundary key={windowDays}>
        <ExperimentResults flagKey={flagKey} flag={flag} windowDays={windowDays} />
      </QueryErrorBoundary>
    </div>
  )
}

function ExperimentResults({ flagKey, flag, windowDays }: { flagKey: string; flag: any; windowDays: number }) {
  const analysis = useQuery(api.experiments.analyzeAll, { flagKey, windowDays, controlVariant: (flag as any).controlVariant || undefined })
  const timeline = useQuery(api.experiments.exposureTimeline, { flagKey })

  if (!analysis || !timeline) {
    return <div className="py-8"><Loading /></div>
  }

  const controlVariant = analysis.controlVariant ?? analysis.variants[0]?.variant ?? ''

  return (
    <div className="space-y-6">
      <ExposureSummary variants={analysis.variants} timeline={timeline} variantDescriptions={(flag as any).variantDescriptions as Record<string, string> | undefined} />
      {timeline.length > 0 && <ExposureChart timeline={timeline} />}
      <ForestPlot metrics={analysis.metrics} controlVariant={controlVariant} variantDescriptions={(flag as any).variantDescriptions as Record<string, string> | undefined} />
      <AuditTimeline flagKey={flagKey} />
    </div>
  )
}

function Loading() {
  return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
}

function ExposureSummary({ variants, timeline, variantDescriptions }: {
  variants: { variant: string; users: number; exposedAt: number | null }[]
  timeline: { date: string; variant: string; cumulative: number }[]
  variantDescriptions?: Record<string, string>
}) {
  const totalUsers = variants.reduce((s, v) => s + v.users, 0)
  const firstDate = timeline.length > 0 ? timeline[0].date : null
  const daysSinceStart = firstDate ? Math.max(1, Math.ceil((Date.now() - new Date(firstDate).getTime()) / 86_400_000)) : 0

  return (
    <div className="grid grid-cols-2 gap-3" style={{ gridTemplateColumns: `1fr repeat(${variants.length}, 1fr)` }}>
      <div className="border border-border rounded-lg bg-card p-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Exposures</div>
        <div className="text-3xl font-bold tabular-nums mt-1">{totalUsers.toLocaleString()}</div>
        {firstDate && <div className="text-xs text-muted-foreground mt-1">Started {firstDate} ({daysSinceStart}d ago)</div>}
      </div>
      {variants.map(v => (
        <div key={v.variant} className="border border-border rounded-lg bg-card p-4">
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-mono">{v.variant}</div>
          <div className="text-3xl font-bold tabular-nums mt-1">{v.users.toLocaleString()}</div>
          {variantDescriptions?.[v.variant] && (
            <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{variantDescriptions[v.variant]}</div>
          )}
        </div>
      ))}
    </div>
  )
}

const VARIANT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#f43f5e', '#06b6d4', '#84cc16']

function ExposureChart({ timeline }: { timeline: { date: string; variant: string; cumulative: number }[] }) {
  const { variants, dates, maxVal, series } = useMemo(() => {
    const variantSet = new Set<string>()
    const dateSet = new Set<string>()
    for (const row of timeline) { variantSet.add(row.variant); dateSet.add(row.date) }
    const variants = [...variantSet].sort()
    const dates = [...dateSet].sort()
    const maxVal = Math.max(...timeline.map(r => r.cumulative), 1)
    const lookup = new Map(timeline.map(r => [`${r.date}:${r.variant}`, r.cumulative]))
    const series = variants.map(v => dates.map(d => lookup.get(`${d}:${v}`) ?? 0))
    return { variants, dates, maxVal, series }
  }, [timeline])

  if (dates.length < 2) return null

  const W = 800, H = 160, PL = 50, PR = 16, PT = 8, PB = 24
  const chartW = W - PL - PR, chartH = H - PT - PB

  return (
    <div className="border border-border rounded-lg bg-card p-5">
      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Cumulative Exposures</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 200 }}>
        {/* Y gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map(frac => {
          const y = PT + chartH * (1 - frac)
          return (
            <g key={frac}>
              <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="currentColor" className="text-border" strokeWidth={0.5} />
              <text x={PL - 6} y={y + 3} textAnchor="end" className="fill-muted-foreground" fontSize={9}>{Math.round(maxVal * frac)}</text>
            </g>
          )
        })}
        {/* Lines */}
        {series.map((data, vi) => {
          const points = data.map((val, di) => {
            const x = PL + (di / (dates.length - 1)) * chartW
            const y = PT + chartH * (1 - val / maxVal)
            return `${x},${y}`
          }).join(' ')
          return <polyline key={vi} points={points} fill="none" stroke={VARIANT_COLORS[vi % VARIANT_COLORS.length]} strokeWidth={2} />
        })}
        {/* X labels */}
        {dates.filter((_, i) => i === 0 || i === dates.length - 1 || (dates.length > 7 && i === Math.floor(dates.length / 2))).map(d => {
          const di = dates.indexOf(d)
          const x = PL + (di / (dates.length - 1)) * chartW
          return <text key={d} x={x} y={H - 4} textAnchor="middle" className="fill-muted-foreground" fontSize={9}>{d.slice(5)}</text>
        })}
      </svg>
      <div className="flex gap-4 mt-2 justify-center">
        {variants.map((v, i) => (
          <div key={v} className="flex items-center gap-1.5 text-xs">
            <div className="w-3 h-0.5 rounded" style={{ backgroundColor: VARIANT_COLORS[i % VARIANT_COLORS.length] }} />
            <span className="font-mono text-muted-foreground">{v}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Forest Plot ───────────────────────────────────────────────────────────────
// Horizontal CI bars sorted by |lift|. Each row: metric name, CI bar centered
// on 0%, point estimate dot, lift% and SIG badge on the right.

type MetricRow = NonNullable<ReturnType<typeof useQuery<typeof api.experiments.analyzeAll>>>['metrics'][number]

function computeCI(row: MetricRow): { liftLo: number; liftHi: number } | null {
  const { controlUsers: n1, controlConverters: c1, variantUsers: n2, variantConverters: c2 } = row
  if (n1 < 5 || n2 < 5) return null
  const p1 = c1 / n1, p2 = c2 / n2
  const se = Math.sqrt(p1 * (1 - p1) / n1 + p2 * (1 - p2) / n2)
  const delta = p2 - p1
  const lo = delta - 1.96 * se, hi = delta + 1.96 * se
  if (p1 === 0) return null
  return { liftLo: lo / p1, liftHi: hi / p1 }
}

function ForestPlot({ metrics, controlVariant, variantDescriptions }: { metrics: MetricRow[]; controlVariant: string; variantDescriptions?: Record<string, string> }) {
  // Deduplicate: for multi-variant experiments, pick the variant with highest |lift| per metric
  const bestPerMetric = useMemo(() => {
    const map = new Map<string, MetricRow>()
    for (const row of metrics) {
      const existing = map.get(row.metric)
      if (!existing || Math.abs(row.liftVsControl ?? 0) > Math.abs(existing.liftVsControl ?? 0)) {
        map.set(row.metric, row)
      }
    }
    return [...map.values()].sort((a, b) => Math.abs(b.liftVsControl ?? 0) - Math.abs(a.liftVsControl ?? 0))
  }, [metrics])

  if (bestPerMetric.length === 0) {
    return (
      <div className="border border-border rounded-lg bg-card px-5 py-3 text-sm text-muted-foreground">
        No metric activity yet.
      </div>
    )
  }

  // Compute scale: find the widest CI extent
  const allCIs = bestPerMetric.map(r => ({ row: r, ci: computeCI(r) }))
  let maxAbs = 0.5
  for (const { row, ci } of allCIs) {
    if (ci) { maxAbs = Math.max(maxAbs, Math.abs(ci.liftLo), Math.abs(ci.liftHi)) }
    if (row.liftVsControl != null) maxAbs = Math.max(maxAbs, Math.abs(row.liftVsControl))
  }
  maxAbs = Math.min(maxAbs * 1.2, 3)

  const pctToX = (lift: number) => 50 + (lift / maxAbs) * 50

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <div className="px-5 py-3 border-b border-border">
        <div className="text-xs text-muted-foreground uppercase tracking-wider">
          Relative Lift vs <span className="font-mono">{controlVariant}</span> (control) — sorted by effect size
        </div>
      </div>

      {/* Scale header */}
      <div className="flex items-center px-5 py-2 border-b border-border text-[10px] text-muted-foreground">
        <div className="w-44 shrink-0" />
        <div className="flex-1 relative h-4">
          <span className="absolute left-0">{(-maxAbs * 100).toFixed(0)}%</span>
          <span className="absolute left-1/2 -translate-x-1/2">0%</span>
          <span className="absolute right-0">{(maxAbs * 100).toFixed(0)}%</span>
        </div>
        <div className="w-24 shrink-0" />
      </div>

      {allCIs.map(({ row, ci }, i) => {
        const lift = row.liftVsControl
        const isPos = (lift ?? 0) >= 0
        const color = row.significant ? (isPos ? 'text-emerald-600' : 'text-red-600') : 'text-muted-foreground'
        const barColor = row.significant ? (isPos ? 'bg-emerald-500' : 'bg-red-500') : 'bg-muted-foreground/30'
        const dotColor = row.significant ? (isPos ? 'bg-emerald-600' : 'bg-red-600') : 'bg-muted-foreground'

        return (
          <div key={`${row.metric}:${row.variant}:${i}`} className={cn('flex items-center px-5 py-2.5 border-b border-border/40', row.significant && 'bg-emerald-500/[0.03]')}>
            {/* Metric name */}
            <div className="w-44 shrink-0 pr-3">
              <div className={cn('text-xs font-mono truncate', row.significant ? 'font-semibold' : '')}>{row.metric}</div>
              {metrics.filter(m => m.metric === row.metric).length > 1 && (
                <div className="text-[10px] text-muted-foreground font-mono" title={variantDescriptions?.[row.variant] ?? undefined}>{row.variant}</div>
              )}
            </div>

            {/* CI bar area */}
            <div className="flex-1 relative h-6">
              {/* Zero line */}
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border" />

              {ci ? (
                <>
                  {/* CI bar */}
                  <div
                    className={cn('absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full', barColor, row.significant ? 'opacity-100' : 'opacity-40')}
                    style={{
                      left: `${Math.max(0, Math.min(100, pctToX(ci.liftLo)))}%`,
                      right: `${Math.max(0, 100 - Math.min(100, pctToX(ci.liftHi)))}%`,
                    }}
                  />
                  {/* Point estimate dot */}
                  {lift != null && (
                    <div
                      className={cn('absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full', dotColor)}
                      style={{ left: `${Math.max(2, Math.min(98, pctToX(lift)))}%` }}
                    />
                  )}
                </>
              ) : lift != null ? (
                <div
                  className={cn('absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 rounded-full', dotColor)}
                  style={{ left: `${Math.max(2, Math.min(98, pctToX(lift)))}%` }}
                />
              ) : null}
            </div>

            {/* Lift + SIG */}
            <div className="w-24 shrink-0 text-right">
              <span className={cn('text-xs tabular-nums font-mono', color)}>
                {lift == null ? '-' : `${lift > 0 ? '+' : ''}${(lift * 100).toFixed(1)}%`}
              </span>
              {row.significant && (
                <span className={cn(
                  'ml-1.5 inline-block px-1 py-0.5 text-[9px] font-bold rounded',
                  isPos ? 'text-emerald-700 bg-emerald-500/15' : 'text-red-700 bg-red-500/15'
                )}>SIG</span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Audit Timeline ────────────────────────────────────────────────────────────

const AUDIT_ACTION_STYLE: Record<string, string> = {
  optimizer_target_picked: 'text-violet-600 border-violet-500/30',
  optimizer_variants_generated: 'text-blue-600 border-blue-500/30',
  optimizer_launched: 'text-emerald-600 border-emerald-500/30',
  optimizer_monitor_check: 'text-muted-foreground border-border',
  optimizer_killed_variant: 'text-amber-600 border-amber-500/30',
  optimizer_shipped: 'text-emerald-700 border-emerald-500/40',
  optimizer_cleanup: 'text-muted-foreground border-border',
  optimizer_aborted: 'text-red-600 border-red-500/30',
}

function AuditTimeline({ flagKey }: { flagKey: string }) {
  const [open, setOpen] = useState(false)
  const trail = useQuery(api.experiments.flagAuditTrail, open ? { flagKey } : 'skip')

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full px-5 py-3 text-left text-xs font-medium text-muted-foreground hover:bg-muted/40 flex items-center gap-2">
        <span className={cn('transition-transform', open && 'rotate-90')}>&#9654;</span>
        Lifecycle &amp; audit trail
      </button>
      {open && (
        <div className="px-5 pb-5">
          {!trail ? <Loading /> : trail.length === 0 ? (
            <div className="text-xs text-muted-foreground">No audit entries yet.</div>
          ) : (
            <ol className="relative border-l border-border ml-2 space-y-4">
              {trail.map((entry, i) => (
                <li key={i} className="ml-4">
                  <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-card border border-border" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn('text-[10px] font-mono px-1.5 py-0.5 rounded border', AUDIT_ACTION_STYLE[entry.action] ?? 'text-muted-foreground border-border')}>{entry.action}</span>
                    <span className="text-[11px] text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</span>
                    {entry.actor && <span className="text-[11px] text-muted-foreground">&middot; {entry.actor}</span>}
                    {entry.castSession && entry.castSession !== 'unknown' && (
                      <a href={entry.castSession} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline">cast session</a>
                    )}
                  </div>
                  {entry.note ? (
                    <div className="mt-2 text-sm prose prose-sm dark:prose-invert max-w-none [&_table]:text-xs [&_h2]:text-sm [&_h2]:mt-2 [&_h2]:mb-1 [&_p]:my-1 [&_ul]:my-1">
                      <ReactMarkdown>{entry.note}</ReactMarkdown>
                    </div>
                  ) : (entry.before != null || entry.after != null) ? (
                    <div className="mt-1 text-[11px] font-mono text-muted-foreground">
                      {entry.before != null && <div>before: {JSON.stringify(entry.before)}</div>}
                      {entry.after != null && <div>after: {JSON.stringify(entry.after)}</div>}
                    </div>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  )
}

// ── Value Card ────────────────────────────────────────────────────────────────

function ValueCard({ flagId, value, isDefault, canRemove, users, hashCount, weight, onSetDefault, onRemoveValue, onAddUser, onRemoveUser }: {
  flagId: Id<'featureFlags'>; value: string; isDefault: boolean; canRemove: boolean
  users: any[]; hashCount: number; weight?: number
  onSetDefault: () => void; onRemoveValue: () => void
  onAddUser: (userId: Id<'users'>) => Promise<any>; onRemoveUser: (userId: Id<'users'>) => Promise<unknown>
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  const searchResults = useQuery(api.featureFlags.searchUsers, searchQuery.length >= 2 ? { query: searchQuery, flagId, limit: 6 } : 'skip')

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) setShowDropdown(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleAdd = async (userId: Id<'users'>) => {
    await onAddUser(userId)
    setSearchQuery('')
    setShowDropdown(false)
  }

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="text-base font-mono font-semibold">{value}</span>
          {isDefault && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-foreground text-background font-medium">default</span>}
          {weight !== undefined && <span className="text-xs text-muted-foreground font-mono">{weight}%</span>}
          {hashCount > 0 && <span className="text-xs text-muted-foreground">{hashCount} hash-bucketed</span>}
        </div>
        <div className="flex items-center gap-2">
          {!isDefault && <Button size="sm" variant="ghost" className="text-xs" onClick={onSetDefault}>Set as default</Button>}
          {canRemove && <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-600 h-7 w-7 p-0" onClick={onRemoveValue}><Trash2 className="h-3.5 w-3.5" /></Button>}
        </div>
      </div>
      <div className="px-5 py-3 space-y-2">
        {users.map((user: any) => (
          <div key={user.userId} className="flex items-center justify-between py-1.5">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center"><Users className="h-3.5 w-3.5 text-muted-foreground" /></div>
              <div>
                <p className="text-sm">{user.name || 'No name'}</p>
                <p className="text-xs text-muted-foreground">{user.email}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-600" onClick={() => { if (confirm('Remove this user?')) onRemoveUser(user.userId) }}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}

        <div ref={searchRef} className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Add user..." value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setShowDropdown(true) }} onFocus={() => setShowDropdown(true)} className="pl-9 h-8 text-sm" />
          {showDropdown && searchQuery.length >= 2 && (
            <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {!searchResults ? <p className="p-2 text-xs text-muted-foreground">Searching...</p>
                : searchResults.length === 0 ? <p className="p-2 text-xs text-muted-foreground">No users found</p>
                : searchResults.map((user) => (
                  <button key={user.userId} onClick={() => handleAdd(user.userId)} className="w-full px-3 py-1.5 text-left hover:bg-muted flex items-center gap-2 border-b border-border last:border-b-0">
                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center"><Users className="h-3 w-3 text-muted-foreground" /></div>
                    <div>
                      <p className="text-sm">{user.name || 'No name'}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
